import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Search, X } from 'lucide-react';
import { capitalizeFirstLetters } from '../util/AddressUtil';
import { useNavigate } from 'react-router-dom';
import { getApiSearchListingAutocomplete } from '../api/search-client';

interface SearchOverlayProps {
    isOpen: boolean;
    onClose: () => void;
    selectedItems: Suggestion[];
    onItemsChange: (items: Suggestion[]) => void;
}

interface Suggestion {
    id: string;
    localityName: string;
    state: string;
    postcode: string;
}

const SearchOverlay: React.FC<SearchOverlayProps> = ({
    isOpen,
    onClose,
    selectedItems,
    onItemsChange
}) => {
    const [keyboardHeight, setKeyboardHeight] = useState<number>(0);
    const [inputValue, setInputValue] = useState<string>('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const hiddenTextRef = useRef<HTMLSpanElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const lineHeightRef = useRef<HTMLDivElement>(null);
    const resultsContainerRef = useRef<HTMLDivElement>(null);

    const lastScrollPosition = useRef<number>(0);

    const handleResize = useCallback(() => {
        const visualViewportHeight = window.visualViewport?.height || window.innerHeight;
        const newKeyboardHeight = window.innerHeight - visualViewportHeight;
        setKeyboardHeight(newKeyboardHeight);
    }, []);

    useEffect(() => {
        window.visualViewport?.addEventListener('resize', handleResize);
        return () => {
            window.visualViewport?.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    useEffect(() => {
        updateLayout();
    }, [keyboardHeight]);

    const updateResultsContainerHeight = () => {
        if (resultsContainerRef.current) {
            const headerHeight = 76;
            const searchButtonHeight = 56;
            const availableHeight = window.innerHeight - headerHeight - searchButtonHeight - keyboardHeight;
            resultsContainerRef.current.style.maxHeight = `${availableHeight}px`;
            resultsContainerRef.current.style.overflowY = 'auto';
        }
    };

    const updateLayout = useCallback(() => {
        requestAnimationFrame(() => {
            updateInputWidth();
            updateScrollableHeight();
            updateResultsContainerHeight();
        });
    }, [keyboardHeight]);

    const smoothScrollToBottom = () => {
        if (scrollContainerRef.current && containerRef.current) {
            const scrollContainer = scrollContainerRef.current;
            const innerContainer = containerRef.current;

            const containerStyles = window.getComputedStyle(innerContainer);

            const fullScrollHeight = innerContainer.offsetHeight +
                parseFloat(containerStyles.paddingTop) +
                parseFloat(containerStyles.paddingBottom);

            scrollContainer.scrollTo({
                top: fullScrollHeight,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        updateLayout();
    }, [selectedItems, updateLayout]);

    useEffect(() => {
        requestAnimationFrame(() => {
            updateInputWidth();
        });
    }, [inputValue]);

    const fetchSuggestions = async (query: string): Promise<Suggestion[]> => {
        try {
            const response = await getApiSearchListingAutocomplete({ query });
            return response;
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            return [];
        }
    };

    const updateInputWidth = () => {
        if (hiddenTextRef.current && inputRef.current) {
            const inputStyles = window.getComputedStyle(inputRef.current);
            const inputPaddingLeft = parseFloat(inputStyles.paddingLeft);
            const inputPaddingRight = parseFloat(inputStyles.paddingRight);

            hiddenTextRef.current.textContent = inputValue || inputRef.current.placeholder;
            const textWidth = hiddenTextRef.current.getBoundingClientRect().width;
            const newWidth = Math.max(1, textWidth + inputPaddingLeft + inputPaddingRight + 1);

            inputRef.current.style.width = `${newWidth}px`;
        }
    };

    const calculateTotalLineHeight = (): number => {
        if (lineHeightRef.current && containerRef.current) {
            const styles = window.getComputedStyle(lineHeightRef.current);
            const containerStyles = window.getComputedStyle(containerRef.current);

            const lineHeight = parseFloat(styles.lineHeight);
            const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
            const marginBottom = parseFloat(styles.marginBottom);

            const containerVerticalPadding = parseFloat(containerStyles.paddingTop) + parseFloat(containerStyles.paddingBottom);

            const totalLineHeight = lineHeight + verticalPadding + marginBottom;

            return totalLineHeight + (containerVerticalPadding / 4);
        }
        return 40;
    };

    const updateScrollableHeight = () => {
        if (scrollContainerRef.current && containerRef.current) {
            const containerHeight = containerRef.current.getBoundingClientRect().height;
            const totalLineHeight = calculateTotalLineHeight();
            const maxHeight = 4 * totalLineHeight;
            if (containerHeight > maxHeight) {
                scrollContainerRef.current.style.maxHeight = `${maxHeight}px`;
                scrollContainerRef.current.style.overflowY = 'auto';
            } else {
                scrollContainerRef.current.style.maxHeight = 'none';
                scrollContainerRef.current.style.overflowY = 'visible';
            }
        }
    };

    const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        if (value) {
            const fetchedSuggestions = await fetchSuggestions(value);
            setSuggestions(fetchedSuggestions);
        } else {
            setSuggestions([]);
        }
        requestAnimationFrame(smoothScrollToBottom);
    };

    const handleSuggestionClick = (suggestion: Suggestion) => {
        const isAlreadySelected = selectedItems.some(item => item.id === suggestion.id);
        if (isAlreadySelected) {
            onItemsChange(selectedItems.filter(item => item.id !== suggestion.id));
        } else {
            onItemsChange([...selectedItems, suggestion]);
        }
        setInputValue('');
        setSuggestions([]);
        inputRef.current?.focus();
        requestAnimationFrame(smoothScrollToBottom);
    };


    const handleRemoveItem = (itemToRemove: Suggestion) => {
        onItemsChange(selectedItems.filter(item => item.id !== itemToRemove.id));
        inputRef.current?.focus();
    };

    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const isClickable = (e.target as HTMLElement).closest('[data-selected-item]') || (e.target as HTMLElement).tagName === 'INPUT';

        if (!isClickable) {
            e.preventDefault();
            inputRef.current?.focus();
        }
    };

    const navigate = useNavigate();

    const handleSearch = () => {
        const localityIds = selectedItems.map(item => item.id);
        const searchParams = new URLSearchParams();
        searchParams.append('localities', localityIds.join(','));

        navigate(`/search-results?${searchParams.toString()}`);
        onClose();
    };

     return (
        <div
            className={`fixed inset-0 bg-white z-[70] flex flex-col transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
            <div className="flex items-start p-4 border-b border-gray-200">
                <button onClick={onClose} className="flex-shrink-0 mr-4 mt-3">
                    <ArrowLeft className="h-6 w-6" />
                </button>
                <div
                    className="flex-grow overflow-hidden border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-[#24ccf2]"
                    onClick={handleContainerClick}
                >
                    <div ref={scrollContainerRef} className="overflow-y-auto" style={{ maxHeight: '160px' }}>
                        <div ref={containerRef} className="flex flex-wrap items-center gap-2 py-2 px-4">
                            {selectedItems.map((item, index) => (
                                <div
                                    key={item.id}
                                    data-selected-item
                                    className="bg-[#24ccf2]/10 text-[#2476f2] rounded-full px-3 py-1 flex items-center flex-shrink-0"
                                    ref={index === 0 ? lineHeightRef : null}
                                >
                                    <span>{capitalizeFirstLetters(item.localityName)}</span>
                                    <button
                                        onClick={() => handleRemoveItem(item)}
                                        className="text-[#2476f2]/70 hover:text-[#2476f2] ml-2"
                                        type="button"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ))}
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputValue}
                                onChange={handleInputChange}
                                placeholder={selectedItems.length === 0 ? "Search by suburb..." : ""}
                                className="outline-none flex-grow-0 flex-shrink-1 py-1 px-2"
                                style={{ width: '1px' }}
                                autoComplete="off"
                                autoCapitalize="off"
                                autoCorrect="off"
                                spellCheck="false"
                                data-form-type="other"
                            />
                            <span
                                ref={hiddenTextRef}
                                aria-hidden="true"
                                className="absolute opacity-0 overflow-hidden whitespace-pre flex-grow-0 flex-shrink-1"
                                style={{
                                    visibility: 'hidden',
                                    position: 'absolute',
                                    left: '-9999px',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div ref={resultsContainerRef} className="flex-grow overflow-auto">
                {suggestions.length > 0 && (
                    <ul className="px-4">
                        {suggestions.map((suggestion) => {
                            const isSelected = selectedItems.some(item => item.id === suggestion.id);
                            return (
                                <li
                                    key={suggestion.id}
                                    className={`py-4 px-4 hover:bg-gray-100 cursor-pointer border-b border-gray-200 flex items-center justify-between ${
                                        isSelected ? 'bg-[#24ccf2]/10' : ''
                                    }`}
                                    onClick={() => handleSuggestionClick(suggestion)}
                                >
                                    <span>
                                        {capitalizeFirstLetters(suggestion.localityName)}, {suggestion.state} {suggestion.postcode}
                                    </span>
                                    {isSelected && <Check className="h-5 w-5 text-[#2476f2]" />}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            <div
                className="fixed bottom-4 right-4 transition-all duration-300 ease-in-out"
                style={{ bottom: `${keyboardHeight + 16}px` }}
            >
                <button
                    className="bg-gradient-to-r from-[#24ccf2] to-[#2476f2] hover:opacity-90 transition-opacity text-white py-3 px-6 rounded-lg shadow-lg flex items-center"
                    onClick={handleSearch}
                >
                    <Search className="h-5 w-5 mr-2" />
                    Search
                </button>
            </div>
        </div>
    );
};


export default SearchOverlay;